'use client';

import type { Card, CoreConfiguration } from '@adyen/adyen-web';
import { useCallback, useEffect, useState } from 'react';
import { getCartBrandsForLocale, getCountry, getLocale } from '@libs/core';
import { waitForElement } from '../utils';
import { adyenBaseConfiguration, adyenCardPlaceholders, adyenCardStyles } from './adyenConfig';
import type { CardState, useAdyenInterface } from './types';
const modifyNameOnCardField = async (signal: AbortSignal) => {
  const nameOnCardField = await waitForElement('.adyen-checkout__card__holderName__input');
  if (nameOnCardField && !signal.aborted) {
    nameOnCardField.setAttribute('maxlength', '30');
    nameOnCardField.removeAttribute('required');
  }
};
const brands = getCartBrandsForLocale(getLocale()).filter(item => item !== 'mastercard');
export const useAdyen = (): useAdyenInterface => {
  const [adyenForm, setAdyenForm] = useState<Card>();
  const [adyenFormState, setAdyenFormState] = useState<CardState>();
  const handleChange = useCallback((state: CardState) => {
    setAdyenFormState(state);
  }, []);
  useEffect(() => {
    const abortController = new AbortController();
    const initializeAdyen = async () => {
      try {
        const {
          AdyenCheckout,
          Card
        } = await import('@adyen/adyen-web');
        const configuration: CoreConfiguration = {
          ...adyenBaseConfiguration,
          countryCode: getCountry(),
          onChange: state_0 => {
            handleChange(state_0 as unknown as CardState);
          },
          showPayButton: false
        };
        const checkout = await AdyenCheckout(configuration);
        if (abortController.signal.aborted) {
          return;
        }
        const component = new Card(checkout, {
          brands,
          hasHolderName: true,
          holderNameRequired: true,
          placeholders: adyenCardPlaceholders,
          styles: adyenCardStyles
        }).mount('#adyen-card-container');
        setAdyenForm(component);
        await modifyNameOnCardField(abortController.signal);
      } catch (error) {
        console.error('Error initializing Adyen:', error);
      }
    };
    initializeAdyen();
    return () => abortController.abort();
  }, [handleChange]);
  return {
    adyenForm,
    adyenFormState
  };
};